/**
 * # The whole pricing-list contract, written down once
 *
 * Every URL this feature calls, every refusal code it recognises, and the one
 * field name that attaches a list to a resource. Nothing else in `Pages/Pricing`
 * builds a path from a string literal, and nothing outside it builds one at all.
 *
 * That rule exists because the backend is being built in parallel with this
 * screen. The model is settled — a tenant-scoped `ResourcePricingList`, rules
 * that belong to the list rather than to a resource, and a `pricingListId` on
 * the resource — but the exact paths are not, and a feature that spelled
 * `/pricing-lists` in nine components would need nine edits and a reviewer who
 * could be sure none was missed. Here it is one edit and a diff that fits on a
 * screen.
 *
 * The refusal codes live here for the same reason and not by accident: a code is
 * as much a part of the wire contract as a path is, and the two change together.
 * When the API renames `RESOURCE_PRICING_LIST_MISSING`, the sentence the venue
 * reads must move with it, and it is one file that has to be right.
 *
 * ## What is deliberately NOT here
 *
 * Any English. `lib/refusals.ts` turns a code into an i18n key; this file knows
 * the codes and nothing about how they read.
 */

/** A tenant's rate cards. `GET`/`POST`, then `GET`/`PATCH`/`DELETE` per id. */
export const PRICING_LISTS = '/pricing-lists';

/**
 * The rules of one list. The collection a rule is created in and the row it is
 * edited on — `DELETE` deactivates rather than destroys, which is why the rule
 * editor's button says so.
 */
export function pricingListPath(listId: string): string {
  return `${PRICING_LISTS}/${listId}`;
}

export function pricingListRulesPath(listId: string): string {
  return `${pricingListPath(listId)}/rules`;
}

export function pricingListRulePath(listId: string, ruleId: string): string {
  return `${pricingListRulesPath(listId)}/${ruleId}`;
}

/**
 * `/resources` — read to answer "which resources use this list?".
 *
 * The write that attaches one is `PATCH /resources/:id`, and it is deliberately
 * NOT spelled here: the resource form already owns that path, and a second
 * module building it would be a second place for it to drift.
 */
export const RESOURCES = '/resources';

/**
 * The field on `CreateResourceDto` / `UpdateResourceDto` that points a resource
 * at its rate card.
 *
 * A named constant rather than a literal in the two form bodies, because
 * `forbidNonWhitelisted` is on: a key the DTO does not declare is a 400 naming a
 * JSON field the manager never typed, and the mistake would be made twice — once
 * on create and once on edit — from two files that would then disagree.
 */
export const PRICING_LIST_FIELD = 'pricingListId';

/**
 * Where a manager goes to run their rate cards. A route rather than a path on
 * the API, and here beside the API paths because both answers to "where do price
 * lists live?" should be found in the same place.
 *
 * A Settings tab rather than a screen of its own: a rate card is a tenant-level
 * registry that every other screen assumes already exists — exactly what the
 * Spaces, Branches and Devices tabs are — and the controller behind it carries
 * no `@RequireScope` for the same reason. It is deliberately NOT under Catalog:
 * that screen is gated on `menu.view` and is about things a guest orders off a
 * menu, and an hourly rate for a PlayStation is neither.
 */
export const PRICING_LISTS_ROUTE = '/settings/pricing';

/**
 * Which card the screen should open on, carried in the query string.
 *
 * The same shape the staff list uses to hand a person to the assignments tab,
 * and for the same reason: a refusal that says "add a rate to Standard rates"
 * has to land on Standard rates, and a screen that opened on whichever card
 * happened to be first would make the operator find it themselves — which is
 * the failure this whole change is about, one level down.
 *
 * A query parameter rather than a second route segment because the tab segment
 * is already spoken for by `useRoutedTabs`, and because the selection is a
 * *view* of the tab rather than a different tab.
 */
export const PRICING_LIST_PARAM = 'list';

export function pricingListRoute(listId?: string | null): string {
  return listId
    ? `${PRICING_LISTS_ROUTE}?${PRICING_LIST_PARAM}=${encodeURIComponent(listId)}`
    : PRICING_LISTS_ROUTE;
}

// ---------------------------------------------------------------------------
// The refusals
// ---------------------------------------------------------------------------

/**
 * The resource points at no list at all.
 *
 * The first of three and the newest: it did not exist while rules hung off the
 * resource, and it is the one a venue meets most, because a PlayStation created
 * before there were any lists has nothing attached. Its fix is not "write a
 * rule" — it is "choose the rate card you already wrote for the other nine".
 */
export const LIST_MISSING = 'RESOURCE_PRICING_LIST_MISSING';

/**
 * A list is attached and nothing in it is in force — empty, or every rule
 * switched off. Fixed inside the list, and not by touching the resource.
 */
export const RULE_MISSING = 'RESOURCE_PRICING_RULE_MISSING';

/**
 * Rules are in force and none of their day/time windows covers this moment.
 * The list is right, the windows are too narrow. Also fixed inside the list —
 * but by widening a window rather than by adding a rate, which is why it is not
 * the same sentence as the one above.
 */
export const RULE_OUT_OF_WINDOW = 'RESOURCE_PRICING_RULE_OUT_OF_WINDOW';

/**
 * Two active rules of equal priority could both apply at the same moment.
 *
 * A 409 at write time rather than a warning, because `selectRule` sorts by
 * priority and takes the first, and the rows reach it in whatever order Postgres
 * returns — so the guest is quoted one of two prices and nobody can say which in
 * advance. The overlap test is `start <= otherEnd && otherStart <= end`, closed
 * at both ends, so 10:00–12:00 and 12:00–14:00 collide at 12:00. That last part
 * is the one a venue writing naive adjacent windows will hit.
 *
 * It is now a property of the *list*: the two rules that collide are siblings in
 * one rate card, so the collision is found once when the card is edited rather
 * than separately on each of the ten PlayStations pointing at it.
 */
export const RULE_AMBIGUOUS = 'RESOURCE_PRICING_RULE_AMBIGUOUS';

/**
 * A card cannot be retired while a resource still charges from it.
 *
 * Carries `attachedResourceCount` and up to five `attachedResourceNames`, which
 * is the entire reason this is a refusal a person can act on rather than a 409
 * they read as a bug: "Move PS5 #1, PS5 #2 and 4 others first" names the work.
 */
export const LIST_IN_USE = 'RESOURCE_PRICING_LIST_IN_USE';

/**
 * A retired card cannot be attached to anything new.
 *
 * Reachable even though the picker filters retired cards out, because the
 * picker's copy of the list can be half an hour old — someone else retires the
 * card between one manager opening the resource form and pressing Save.
 */
export const LIST_DEACTIVATED = 'RESOURCE_PRICING_LIST_DEACTIVATED';

/** The card is gone. The editor that was showing it has to stop showing it. */
export const LIST_NOT_FOUND = 'RESOURCE_PRICING_LIST_NOT_FOUND';

/** The rule is no longer on this card — deleted, or moved, while the form was open. */
export const RULE_NOT_FOUND = 'RESOURCE_PRICING_RULE_NOT_FOUND';

/**
 * Every code this client can say a sentence about. Anything else gets the
 * generic sentence — never the server's own text; see `lib/refusals.ts`.
 */
export const KNOWN_REFUSAL_CODES = [
  LIST_MISSING,
  RULE_MISSING,
  RULE_OUT_OF_WINDOW,
  RULE_AMBIGUOUS,
  LIST_IN_USE,
  LIST_DEACTIVATED,
  LIST_NOT_FOUND,
  RULE_NOT_FOUND,
] as const;
